import { createContext, useContext, ReactNode, useState } from 'react';
import { useLocalStorage } from '@shared/hooks/useLocalStorage';
import './WishlistToast.css';

export interface WishlistItem {
  productId: string;
  name: string;
  price: number;
  imageUrl?: string;
  inStock?: boolean;
}

interface WishlistContextType {
  wishlistItems: WishlistItem[];
  addToWishlist: (item: WishlistItem) => void;
  removeFromWishlist: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
  clearWishlist: () => void;
  getWishlistItemCount: () => number;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

interface WishlistProviderProps {
  children: ReactNode;
}

export const WishlistProvider = ({ children }: WishlistProviderProps) => {
  const [wishlistItems, setWishlistItems] = useLocalStorage<WishlistItem[]>('wishlist', []);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  const addToWishlist = (item: WishlistItem) => {
    setWishlistItems((currentItems) => {
      if (currentItems.some((i) => i.productId === item.productId)) {
        return currentItems;
      }
      return [...currentItems, item];
    });
    showToast('Product added to wishlist');
  };

  const removeFromWishlist = (productId: string) => {
    setWishlistItems((currentItems) => currentItems.filter((item) => item.productId !== productId));
    showToast('Product removed from wishlist');
  };

  const isInWishlist = (productId: string) => {
    return wishlistItems.some((item) => item.productId === productId);
  };

  const clearWishlist = () => {
    setWishlistItems([]);
  };

  const getWishlistItemCount = () => {
    return wishlistItems.length;
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlistItems,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        clearWishlist,
        getWishlistItemCount,
      }}
    >
      {children}
      {toastMessage && (
        <div className="wishlist-toast">
          {toastMessage}
        </div>
      )}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
